import type { ThemeConfig } from 'antd';
import { getColor } from '../constants/colors';

/**
 * Creates the Ant Design theme configuration.
 * Must be called after DOM is ready since it reads CSS variables.
 */
export const createTheme = (): ThemeConfig => ({
    token: {
        // Primary Colors
        colorPrimary: getColor('sky-blue-light'),
        colorSuccess: getColor('success'),
        colorError: getColor('error'),
        colorWarning: getColor('warning'),
        colorInfo: getColor('info'),

        // Typography
        fontFamily: 'Inter Variable',
        fontSize: 14,
        fontSizeHeading5: 13,
        fontWeightStrong: 600,

        // Text Colors
        colorText: getColor('text-primary'),
        colorTextSecondary: getColor('text-secondary'),
        colorTextTertiary: getColor('text-tertiary'),
        colorTextQuaternary: getColor('text-disabled'),
        colorTextPlaceholder: getColor('text-disabled'),

        // Background Colors
        colorBgContainer: getColor('bg-container'),
        colorBgLayout: getColor('bg-layout'),
        colorBgElevated: getColor('bg-container'),

        // Border Colors
        colorBorder: getColor('border'),
        colorBorderSecondary: getColor('border-light'),

        // Border Radius
        borderRadius: 6,
        borderRadiusLG: 8,
        borderRadiusSM: 4,

        // Spacing
        padding: 16,
        paddingLG: 24,
        paddingSM: 12,
        paddingXS: 8,
    },
    components: {
        Typography: {
            colorTextHeading: getColor('text-primary'),
            fontSizeHeading1: 24,
            fontSizeHeading2: 22,
            fontSizeHeading3: 20,
            fontSizeHeading4: 18,
            fontSizeHeading5: 16,
        },
        Layout: {
            siderBg: getColor('bg-container'),
        },
        Table: {
            headerBg: getColor('table-header-bg'),
            headerColor: getColor('table-text'),
            headerSplitColor: 'transparent',
            borderColor: getColor('table-border'),
            rowHoverBg: getColor('table-row-hover'),
            fontSize: 12,
            headerSortHoverBg: getColor('bg-hover'),
            headerSortActiveBg: getColor('bg-secondary'),
            colorText: getColor('table-text'),
            colorTextHeading: getColor('table-text'),
        },
        Pagination: {
            // Basic item styling
            itemActiveBg: 'transparent',
            itemBg: 'transparent',
            itemSize: 32,

            // Colors
            colorPrimary: getColor('sky-blue-light'),
            colorPrimaryHover: getColor('sky-blue-light'),
            colorText: getColor('text-secondary'),
            colorTextDisabled: getColor('text-disabled'),

            // Typography
            fontSize: 14,

            // Layout and spacing
            borderRadius: 6,
        },
        Select: {
            borderRadius: 8,
            controlHeight: 32,
            fontSize: 12,
            colorBorder: getColor('border'),
        },
        Input: {
            borderRadius: 8,
            controlHeight: 32,
            fontSize: 12,
            colorBorder: getColor('border'),
            paddingInline: 16,
        },
        DatePicker: {
            borderRadius: 8,
            controlHeight: 32,
            colorBorder: getColor('border'),
            fontSize: 12,
        },
        Button: {
            borderRadius: 8,
            controlHeight: 40,
            paddingInline: 16,
            fontSize: 14,
            fontWeight: 500,
        },
        Card: {
            borderRadius: 8,
            paddingLG: 24,
            colorBorderSecondary: getColor('border-light'),
        },
        Modal: {
            borderRadius: 8,
            paddingLG: 24,
        },
        Dropdown: {
            borderRadius: 6,
            paddingBlock: 8,
        },
        List: {
            colorText: getColor('text-secondary'),
        },
        Menu: {
            itemSelectedBg: getColor('bg-secondary'),
            borderRadius: 6,
            itemHoverBg: getColor('menu-bg-hover'),
            itemHoverColor: getColor('sky-blue-light'),
        },
        Divider: {
            colorSplit: getColor('border-light'),
        },
        Tooltip: {
            borderRadius: 8,
            fontSize: 12,
            colorBgElevated: getColor('bg-container'),
            colorBgBase: getColor('bg-container'),
            colorBgSpotlight: getColor('bg-container'),
            colorBgContainer: getColor('bg-container'),
            colorTextLightSolid: getColor('text-tooltip'),
            fontWeightStrong: 500,
            boxShadow: '0 0 8px 0 rgba(0, 0, 0, 0.25);',
        },
        Progress: {
            defaultColor: getColor('sky-blue-light'),
        },
        Spin: {
            dotSize: 32,
            colorPrimary: getColor('sky-blue-light'),
        },
    },
});

// Default theme instance (for backwards compatibility)
// Note: This will have empty values if imported before DOM is ready
export const theme: ThemeConfig = createTheme();

// Export commonly used theme values for components
export const THEME_TOKENS = {
    spacing: {
        xs: 8,
        sm: 12,
        md: 16,
        lg: 24,
        xl: 32,
    },
    borderRadius: {
        sm: 4,
        md: 6,
        lg: 8,
    },
    typography: {
        fontSize: {
            xs: 12,
            sm: 13,
            base: 14,
            lg: 16,
            xl: 18,
        },
        fontWeight: {
            normal: 400,
            medium: 500,
            semibold: 600,
            bold: 700,
        },
    },
} as const;
